import mixpanel, { Config } from 'mixpanel-browser'
import { v4 as uuid } from 'uuid'

import { buildStorageService } from './storageService'
import {
  getAccessToken,
  getFromSessionStorage,
  getSessionId
} from '@/utils/helpers/storageHelpers'

import { appSettings } from '@/shared/consts/appSettings'
import { propsNames } from '@/shared/consts/mixpanel/propsNames'
import { eventNames } from '@/shared/consts/mixpanel/eventNames'
import { useScheduleStore } from '@/store/useScheduleStore'
import { useUserStore } from '@/store/useUserStore'
import {
  hasWebViewCookiesPermitted,
  isWebView
} from '@/utils/helpers/webViewHelper'

interface SendEventProps {
  eventName: keyof typeof eventNames
  data?: Partial<Record<keyof typeof propsNames, any>>
}

let isLoaded = false

export const initMixpanel = () => {
  if (isLoaded || (isWebView() && !hasWebViewCookiesPermitted())) return

  const config: Partial<Config> = {
    loaded: (mixpanel) => {
      isLoaded = true
      const deviceId = mixpanel.get_property('$device_id') || uuid()
      buildStorageService(localStorage).setItem('deviceId', deviceId)
    },
    stop_utm_persistence: true,
    record_sessions_percent: 100,
    record_mask_text_selector: '',
    record_block_selector: ''
  }
  mixpanel.init(appSettings.mixPanelToken, config)
  const utms = getUtms()
  mixpanel.register(utms)
}

export const registerMixpanelUtms = () => {
  if (!isLoaded) return
  const utms = getUtms()
  if (Object.keys(utms || {}).length > 0) {
    mixpanel.register(utms)
  }
}

const getUtms = () => {
  const utms = getFromSessionStorage('utms')

  if (!Object.keys(utms || {}).length) {
    return {
      utm_source: undefined,
      utm_medium: undefined,
      utm_campaign: undefined,
      utm_term: undefined,
      utm_content: undefined
    }
  }
  return utms
}

const replaceKeysForValue = (
  data: Partial<Record<keyof typeof propsNames, any>>
) => {
  return Object.keys(data).reduce(
    (acc, key) => {
      const formattedKey = propsNames[key] ?? key
      acc[formattedKey] = data[key]
      return acc
    },
    {} as Record<string, any>
  )
}

function getPaymentMethod(healthPlan) {
  if (!healthPlan?.id) return undefined
  return healthPlan.isPrivate ? 'PARTICULAR' : 'PLANO'
}

const getCommonData = () => {
  const nav = typeof navigator !== 'undefined' ? (navigator as any) : undefined
  const connection = nav
    ? nav.connection || nav.mozConnection || nav.webkitConnection
    : undefined
  const pathArray = window.location.pathname.split('/')
  const step = pathArray[pathArray.length - 1]

  return {
    [propsNames.APP]: 'Consultas',
    [propsNames.APP_VERSION]: appSettings.applicationVersion,
    [propsNames.SESSION_ID]: getSessionId(),
    [propsNames.IS_PWA]: Boolean(
      window.matchMedia('(display-mode: standalone)').matches
    ),
    [propsNames.STEP]: step,
    [propsNames.USER_LOGGED]: Boolean(getAccessToken()),
    [propsNames.CONNECTION_TYPE]:
      connection?.type || connection?.effectiveType || 'Desconhecido',
    [propsNames.EXTIMATED_SPEED]: connection?.downlink
      ? `${connection.downlink} Mbps`
      : 'Desconhecida',
    [propsNames.ONLINE_STATUS]: navigator.onLine ? 'Conectado' : 'Desconectado',
    [propsNames.APP_NATIVE]: Boolean(isWebView())
  }
}

export const sendEventWithoutStore = ({
  eventName,
  data = {}
}: SendEventProps) => {
  if (!isLoaded) {
    return
  }

  const eventNameFormatted = eventNames[eventName]

  if (!eventNameFormatted) {
    return
  }

  const dataFormatted = replaceKeysForValue(data)

  mixpanel.track(eventNameFormatted, { ...getCommonData(), ...dataFormatted })
}

export const sendEvent = ({ eventName, data = {} }: SendEventProps) => {
  if (isWebView() && !hasWebViewCookiesPermitted()) {
    return
  }

  const {
    unit,
    slot,
    doctor,
    patient,
    product,
    location,
    flowType,
    healthPlan,
    schedulingId,
    healthCarrier
  } = useScheduleStore.getState()

  const userStore = useUserStore.getState()

  const eventNameFormatted = eventNames[eventName]

  if (!eventNameFormatted || !isLoaded) {
    return
  }

  const dataFormatted = replaceKeysForValue(data)

  const commomData = {
    [propsNames.REQUISITION_CODE]: schedulingId,
    [propsNames.USER_LOGGED_CPF]: userStore.user?.cpf,
    [propsNames.MODALITY]: product?.isDigitalConsultation
      ? 'TELECONSULTA'
      : 'PRESENCIAL',
    [propsNames.FLOW_TYPE]:
      flowType === 'doctor' ? 'MÉDICO ESPECÍFICO' : 'ESPECIALIDADE',
    [propsNames.CONSULTATION_DOCTOR_CODE]: doctor?.id || slot?.doctor.id,
    [propsNames.DOCTOR_NAME]: doctor?.name || slot?.doctor.name,
    [propsNames.CONSULTATION_SPECIALTY_CODE]: product?.id,
    [propsNames.SPECIALTY_DESCRIPTION]: product?.name,
    [propsNames.LOCAL_UF]: location?.address.state,
    [propsNames.LOCAL_RDSL]: location?.id,
    [propsNames.LOCAL_CODE]: location?.idMotion,
    [propsNames.LOCAL_DESCRIPTION]: location?.name,
    [propsNames.LOCAL_TYPE]: location?.type,
    [propsNames.PATIENT_BIRTHDATE]: patient?.birthDate,
    [propsNames.PATIENT_GENDER]: patient?.gender || undefined,
    [propsNames.PAYMENT_METHOD]: getPaymentMethod(healthPlan),
    [propsNames.HEALTH_CARRIER_NAME]: healthCarrier?.name,
    [propsNames.HEALTH_CARRIER_CODE]: healthCarrier?.id,
    [propsNames.HEALTH_PLAN_NAME]: healthPlan?.name,
    [propsNames.HEALTH_PLAN_CODE]: healthPlan?.id,
    [propsNames.UNITY_NAME]: slot?.sector?.unitName || unit?.name,
    [propsNames.CONSULTATION_UNITY_CODE]: slot?.sector?.unitId,
    [propsNames.CONSULTATION_SECTOR_CODE]: slot?.sector?.id
  }

  mixpanel.track(eventNameFormatted, {
    ...getCommonData(),
    ...commomData,
    ...dataFormatted
  })
}
